/**
 * <afroyla-search-band> — light-DOM custom element for the header search band
 * (snippets/afroyla-search-band.liquid). Owns the SINGLE source of truth for
 * open/close: the #afl-search-band mount's `hidden` attribute AND the header
 * SEARCH control's `aria-expanded`. It is the only script that knows about every
 * close path (header click, band close, Esc), so afroyla-header.js only dispatches
 * the toggle intent — see that file's search-toggle handler for the other half of
 * this contract. No focus trap: the band is in-flow, not modal (phase-10-search.md).
 *
 * Also runs the debounced predictive-search fetch (300ms idle, 2-char minimum,
 * AbortController-cancelled). v1 ships with no visible suggestion dropdown (mock
 * parity — phase-10 Unresolved Q2 is pending an owner decision on presentation);
 * the parsed response is cached on the element so a future dropdown can consume it
 * without re-plumbing the fetch.
 */
(function () {
  'use strict';

  if (window.customElements.get('afroyla-search-band')) return;

  var DEBOUNCE_MS = 300;
  var MIN_CHARS = 2;

  class AfroylaSearchBand extends HTMLElement {
    connectedCallback() {
      this.mount = document.getElementById('afl-search-band');
      this.toggleBtn = document.getElementById('afl-search-toggle');
      this.input = this.querySelector('.afl-search-band__input');
      this.closeBtn = this.querySelector('[data-afl-search-close]');
      if (!this.mount || !this.input) return;

      this._debounceTimer = null;
      this._abortController = null;
      this.suggestions = null; // cached predictive payload, advisory-only in v1

      this._onToggle = this.toggle.bind(this);
      this._onKeydown = this._handleKeydown.bind(this);
      this._onInput = this._handleInput.bind(this);
      this._onClose = this.close.bind(this);

      document.addEventListener('afroyla:search:toggle', this._onToggle);
      document.addEventListener('keydown', this._onKeydown);
      this.input.addEventListener('input', this._onInput);
      if (this.closeBtn) this.closeBtn.addEventListener('click', this._onClose);
    }

    disconnectedCallback() {
      document.removeEventListener('afroyla:search:toggle', this._onToggle);
      document.removeEventListener('keydown', this._onKeydown);
    }

    isOpen() {
      return !!this.mount && !this.mount.hidden;
    }

    toggle() {
      if (this.isOpen()) this.close();
      else this.open();
    }

    open() {
      this.mount.hidden = false;
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'true');
      // Focus lands in the input with any existing text selected (state machine:
      // "Band closed -> open").
      this.input.focus();
      this.input.select();
    }

    close() {
      if (!this.isOpen()) return;
      this.mount.hidden = true;
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-expanded', 'false');
        this.toggleBtn.focus();
      }
    }

    _handleKeydown(event) {
      if (event.key === 'Escape' && this.isOpen()) this.close();
    }

    _handleInput() {
      var value = this.input.value.trim();
      if (this._debounceTimer) clearTimeout(this._debounceTimer);
      if (this._abortController) this._abortController.abort();

      if (value.length < MIN_CHARS) {
        this.suggestions = null;
        return;
      }

      var self = this;
      this._debounceTimer = setTimeout(function () {
        self._fetchSuggestions(value);
      }, DEBOUNCE_MS);
    }

    _fetchSuggestions(value) {
      var base =
        (window.Theme && window.Theme.routes && window.Theme.routes.predictive_search_url) ||
        '/search/suggest';
      var url =
        base + (base.indexOf('?') === -1 ? '?' : '&') + 'q=' + encodeURIComponent(value) + '&resources[type]=product';

      this._abortController = new AbortController();
      var self = this;

      fetch(url, { signal: this._abortController.signal, credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (data) {
          self.suggestions = data;
        })
        .catch(function (error) {
          if (error.name !== 'AbortError') self.suggestions = null;
        });
    }
  }

  window.customElements.define('afroyla-search-band', AfroylaSearchBand);
})();
